/**
 * Consulta clínica activa — CLINICAL-CONSULTATION-DATA-SAFETY-P0 · PR-4.
 *
 * Enlace mínimo entre la capa de sesión y la consulta abierta. UN solo hueco,
 * ocupado por `ConsultaPage` mientras su borrador es editable (ni firmado ni en
 * pausa). Solo guarda identidad y callbacks: NUNCA contenido clínico (el texto
 * vive en la memoria de la página, como siempre).
 *
 * Lo usan exactamente dos disparadores: la entrada en el aviso de sesión y el
 * botón "Cerrar sesión" del panel. No es un mecanismo genérico de formularios.
 *
 * La marca de navegación forzada evita que el aviso `beforeunload` de la
 * consulta bloquee un cierre de sesión (por vencimiento, por pérdida de sesión
 * en otra pestaña o el cierre manual ya decidido).
 */

export type ConsultationFlushResult = 'clean' | 'saved' | 'failed' | 'skipped';

interface ActiveConsultation {
  appointmentId: string;
  /** ¿Hay texto o vitales que el servidor todavía no confirmó? */
  isDirty: () => boolean;
  /** Guarda el borrador (la misma ruta estricta de "Guardar borrador"). */
  flush: () => Promise<ConsultationFlushResult>;
}

let active: ActiveConsultation | null = null;
let forcedNavigation = false;

/** Ocupa el hueco; devuelve la función que lo libera (solo si sigue siendo suyo). */
export function registerActiveConsultation(entry: ActiveConsultation): () => void {
  active = entry;
  return () => {
    if (active === entry) active = null;
  };
}

export function isActiveConsultationDirty(): boolean {
  try {
    return active !== null && active.isDirty();
  } catch {
    return false;
  }
}

/** Sin consulta activa → 'clean'. Nunca lanza: un error es 'failed'. */
export async function flushActiveConsultation(): Promise<ConsultationFlushResult> {
  const entry = active;
  if (!entry) return 'clean';
  try {
    return await entry.flush();
  } catch {
    return 'failed';
  }
}

export function markForcedNavigation(): void {
  forcedNavigation = true;
}

export function isForcedNavigation(): boolean {
  return forcedNavigation;
}
